import React, { useMemo, useState } from 'react';
import { Alert, Button, Collapse, Empty, Space, Spin, Table, Tag, Typography } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined, LinkOutlined } from '@ant-design/icons';
import { API_BASE_URL } from '../../utils/config';
import { useAktifYukle } from './biOrtak';

/**
 * Kaynaklar sekmesi (28.10.2026): Power BI'a verilecek rapor bağlantıları — anahtar + XML/JSON adresi (token'lı).
 * Token /bi-v2/kaynaklar'dan yalnız Genel/Finans okuma yetkisiyle gelir; ekranda maskeli durur, kopyalanan adres tamdır.
 * Adresler bu ortamın API kökünden kurulur (dev ve prod token'ları farklıdır).
 */
const { Text, Paragraph } = Typography;
const YER_TUTUCU = '<TOKEN>';

const apiKoku = () => {
  try {
    return new URL(API_BASE_URL, window.location.origin).href.replace(/\/+$/, '');
  } catch {
    return String(API_BASE_URL || '').replace(/\/+$/, '');
  }
};

const maskele = (t) => (t && t.length > 8 ? `${t.slice(0, 4)}${'•'.repeat(8)}${t.slice(-4)}` : '••••••••');

function Adres({ url, gorunen }) {
  return (
    <Text copyable={{ text: url, tooltips: ['Tam adresi kopyala', 'Kopyalandı'] }} style={{ fontSize: 12, wordBreak: 'break-all' }}>
      <a href={url} target="_blank" rel="noreferrer"><LinkOutlined /></a>{' '}
      <code>{gorunen}</code>
    </Text>
  );
}

export default function BiKaynaklar({ api, aktif, seciliKey }) {
  const { veri, yukleniyor, hata } = useAktifYukle(aktif, 'kaynaklar', () => api.get('/kaynaklar'));
  const [goster, setGoster] = useState(false);
  const kok = useMemo(apiKoku, []);

  if (yukleniyor && !veri) return <Spin style={{ display: 'block', margin: 32 }} />;
  if (hata) return <Alert type="error" showIcon message="Kaynaklar alınamadı" description={hata} />;
  if (!veri) return <Empty />;

  const token = veri.token || YER_TUTUCU;
  const tokenGorunen = veri.token ? (goster ? veri.token : maskele(veri.token)) : YER_TUTUCU;
  const adres = (key, ek = '', t = token) => `${kok}${veri.rapor_yolu.replace('{key}', encodeURIComponent(key))}?token=${encodeURIComponent(t)}${ek}`;
  const satir = (key, ek = '') => ({ url: adres(key, ek), gorunen: adres(key, ek, tokenGorunen) });

  const kolonlar = (biv2) => [
    {
      title: 'Rapor', key: 'rapor', width: 260, render: (_, r) => (
        <Space direction="vertical" size={2}>
          <Text strong>{r.ad}</Text>
          <Space size={4} wrap>
            <Tag color={r.key === seciliKey ? 'blue' : 'default'}>{r.key}</Tag>
            {r.kalem_turu && <Tag>{r.kalem_turu}</Tag>}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Power BI (XML)', key: 'xml', render: (_, r) => {
        const x = satir(r.key);
        return <Adres url={x.url} gorunen={x.gorunen} />;
      },
    },
    {
      title: 'JSON', key: 'json', width: 320, render: (_, r) => {
        const x = satir(r.key, '&format=json');
        return <Adres url={x.url} gorunen={x.gorunen} />;
      },
    },
    ...(biv2 ? [{
      title: 'Örnek', key: 'ornek', width: 300, render: (_, r) => {
        const y = satir(r.key, `&yil=${new Date().getFullYear()}`);
        const d = satir(r.key, `&bas=${new Date().getFullYear()}-01&bit=${new Date().getFullYear()}-06`);
        const t = satir(r.key, '&detay=1');
        return (
          <Space direction="vertical" size={2}>
            <Text copyable={{ text: y.url }} style={{ fontSize: 12 }}>Yalnız {new Date().getFullYear()}</Text>
            <Text copyable={{ text: d.url }} style={{ fontSize: 12 }}>Ocak–Haziran {new Date().getFullYear()}</Text>
            <Text copyable={{ text: t.url }} style={{ fontSize: 12 }}>Tüm kolonlar (detay)</Text>
          </Space>
        );
      },
    }] : []),
  ];

  const liste = {
    xml: `${kok}${veri.liste_yolu}?token=${encodeURIComponent(token)}&format=xml`,
    gorunen: `${kok}${veri.liste_yolu}?token=${encodeURIComponent(tokenGorunen)}&format=xml`,
  };

  return (
    <div>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="Power BI bağlantıları"
        description={(
          <>
            Power BI Desktop → Veri al → Web → adresi yapıştırın (XML). Adresler <b>bu ortamın</b> ({kok}) API'sine ve
            token'ına aittir; canlı (erp) adresleri ve token'ı farklıdır, biv2 raporları canlıya alınınca canlı ekranda
            görünür. Kopyala düğmesi token'lı tam adresi kopyalar. Biçim: tutarlar <code>180000,00</code>, tarihler
            <code> 2026-10-27</code>, dönem <code>2026-10</code>.
          </>
        )} />

      {!veri.token_tanimli && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="Bu ortamda BI token'ı tanımlı değil (BI_API_TOKENS)." />}
      {veri.token_tanimli && !veri.token_yetkisi && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="Token'ı görmek için Genel / Finans okuma yetkisi gerekir; adreslerde yer tutucu var." />
      )}

      <Space wrap style={{ marginBottom: 12 }}>
        <Text strong>Token:</Text>
        <Text code copyable={veri.token ? { text: veri.token, tooltips: ['Token\'ı kopyala', 'Kopyalandı'] } : false}>{tokenGorunen}</Text>
        {veri.token && (
          <Button size="small" icon={goster ? <EyeInvisibleOutlined /> : <EyeOutlined />} onClick={() => setGoster((g) => !g)}>
            {goster ? 'Gizle' : 'Göster'}
          </Button>
        )}
        <Text type="secondary" style={{ fontSize: 12 }}>Token'ı paylaşmayın: tüm BI raporlarına okuma erişimi verir.</Text>
      </Space>

      <Paragraph style={{ marginBottom: 8 }}>
        <Text strong>Rapor listesi: </Text><Adres url={liste.xml} gorunen={liste.gorunen} />
      </Paragraph>

      <Table size="small" rowKey="key" pagination={false} dataSource={veri.raporlar} columns={kolonlar(true)}
        scroll={{ x: 'max-content' }} style={{ marginBottom: 16 }}
        locale={{ emptyText: 'Kayıtlı biv2 raporu yok' }} />

      <Collapse size="small" items={[
        {
          key: 'param', label: 'Parametreler',
          children: (
            <Table size="small" rowKey="ad" pagination={false} dataSource={veri.parametreler}
              columns={[
                { title: 'Parametre', dataIndex: 'ad', width: 140, render: (v) => <code>{v}</code> },
                { title: 'Örnek', dataIndex: 'ornek', width: 170, render: (v) => <code>{v}</code> },
                { title: 'Açıklama', dataIndex: 'aciklama' },
              ]} />
          ),
        },
        {
          key: 'v1', label: `Eski (v1) bi-* raporları (${veri.v1.length}) — değiştirilmedi, Power BI'da kullanılanlar bozulmasın diye duruyor`,
          children: (
            <Table size="small" rowKey="key" pagination={false} dataSource={veri.v1} columns={kolonlar(false)}
              scroll={{ x: 'max-content' }} />
          ),
        },
      ]} />
    </div>
  );
}
